import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Shield, UserPlus, X, RefreshCw } from "lucide-react";

type AppRole = "admin" | "member" | "events_admin" | "blog_admin" | "applicant_admin" | "visit_admin";
type Chapter = "washington" | "union" | "all";

interface RoleRow {
  id: string;
  user_id: string;
  role: AppRole;
  chapter: Chapter | null;
}

interface ProfileRow {
  id: string;
  email: string;
}

interface MemberRow {
  user_id: string | null;
  name: string;
  chapter: Chapter;
}

interface UserAggregate {
  user_id: string;
  email: string;
  name: string | null;
  member_chapter: Chapter | null;
  roles: RoleRow[];
}

const ROLE_LABELS: Record<AppRole, string> = {
  admin: "Admin",
  member: "Member",
  events_admin: "Events Admin",
  blog_admin: "Blog Admin",
  applicant_admin: "Applicant Admin",
  visit_admin: "Visit Admin",
};

const ASSIGNABLE_ROLES: { value: AppRole; label: string; chapterScoped: boolean; description: string }[] = [
  { value: "events_admin", label: "Events Admin", chapterScoped: false, description: "User can create and manage events." },
  { value: "blog_admin", label: "Blog Admin", chapterScoped: false, description: "User can create and manage blog posts." },
  { value: "applicant_admin", label: "Applicant Admin", chapterScoped: true, description: "User can review applications and is CC'd on new application notifications for this chapter." },
  { value: "visit_admin", label: "Visit Admin", chapterScoped: true, description: "User can review visit requests and is CC'd on new visit-request notifications for this chapter." },
];

const CHAPTER_LABELS: Record<Chapter, string> = {
  washington: "Washington",
  union: "Union",
  all: "All Chapters",
};

const AdminRolesList = () => {
  const [search, setSearch] = useState("");
  const [assignTarget, setAssignTarget] = useState<UserAggregate | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  useMemo(() => {
    supabase.auth.getSession().then(({ data }) => setCurrentUserId(data.session?.user.id ?? null));
  }, []);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin-roles-overview"],
    queryFn: async () => {
      const [profilesRes, membersRes, rolesRes] = await Promise.all([
        supabase.from("profiles").select("id, email"),
        supabase.from("members").select("user_id, name, chapter"),
        supabase.from("user_roles").select("id, user_id, role, chapter"),
      ]);
      if (profilesRes.error) throw profilesRes.error;
      if (membersRes.error) throw membersRes.error;
      if (rolesRes.error) throw rolesRes.error;

      const profiles = (profilesRes.data ?? []) as ProfileRow[];
      const members = (membersRes.data ?? []) as MemberRow[];
      const roles = (rolesRes.data ?? []) as RoleRow[];

      const memberByUserId = new Map<string, MemberRow>();
      members.forEach((m) => { if (m.user_id) memberByUserId.set(m.user_id, m); });

      const rolesByUserId = new Map<string, RoleRow[]>();
      roles.forEach((r) => {
        const arr = rolesByUserId.get(r.user_id) ?? [];
        arr.push(r);
        rolesByUserId.set(r.user_id, arr);
      });

      const aggregates: UserAggregate[] = profiles.map((p) => {
        const member = memberByUserId.get(p.id);
        return {
          user_id: p.id,
          email: p.email,
          name: member?.name ?? null,
          member_chapter: member?.chapter ?? null,
          roles: rolesByUserId.get(p.id) ?? [],
        };
      });

      // Sort: users with elevated roles first, then alphabetically
      aggregates.sort((a, b) => {
        const aElev = a.roles.some((r) => r.role !== "member");
        const bElev = b.roles.some((r) => r.role !== "member");
        if (aElev !== bElev) return aElev ? -1 : 1;
        return (a.name ?? a.email).localeCompare(b.name ?? b.email);
      });
      return aggregates;
    },
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data ?? [];
    return (data ?? []).filter(
      (u) =>
        u.email.toLowerCase().includes(q) ||
        (u.name ?? "").toLowerCase().includes(q),
    );
  }, [data, search]);

  const revokeRole = async (row: RoleRow, isSelf: boolean) => {
    if (row.role === "admin") {
      toast.error("Admin role cannot be revoked from the UI.");
      return;
    }
    if (isSelf) {
      toast.error("You cannot revoke your own roles.");
      return;
    }
    const { error } = await supabase.from("user_roles").delete().eq("id", row.id);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Role revoked");
    refetch();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              <div>
                <CardTitle>Roles & Permissions</CardTitle>
                <CardDescription>
                  Assign and revoke permissions. Global <strong>Admin</strong> can only be set in the database.
                </CardDescription>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 max-w-sm">
            <Label className="text-xs">Search</Label>
            <Input
              placeholder="Name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {isLoading ? (
            <p className="text-muted-foreground">Loading...</p>
          ) : filtered.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No users match your search.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Chapter</TableHead>
                    <TableHead>Roles</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((u) => {
                    const isSelf = u.user_id === currentUserId;
                    return (
                      <TableRow key={u.user_id}>
                        <TableCell className="font-medium">
                          {u.name ?? <span className="text-muted-foreground italic">(no profile)</span>}
                          {isSelf && <Badge variant="outline" className="ml-2 text-xs">You</Badge>}
                        </TableCell>
                        <TableCell className="text-sm">{u.email}</TableCell>
                        <TableCell className="capitalize text-sm text-muted-foreground">
                          {u.member_chapter ?? "—"}
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1.5">
                            {u.roles.length === 0 && (
                              <span className="text-xs text-muted-foreground">No roles</span>
                            )}
                            {u.roles.map((r) => {
                              const locked = r.role === "admin" || r.role === "member" || isSelf;
                              return (
                                <Badge
                                  key={r.id}
                                  variant={r.role === "admin" ? "default" : "secondary"}
                                  className="gap-1 pl-2 pr-1"
                                >
                                  <span>
                                    {ROLE_LABELS[r.role]}
                                    {r.chapter && (r.role === "applicant_admin" || r.role === "visit_admin") && (
                                      <span className="ml-1 opacity-75">· {CHAPTER_LABELS[r.chapter]}</span>
                                    )}
                                  </span>
                                  {!locked && (
                                    <button
                                      onClick={() => revokeRole(r, isSelf)}
                                      className="ml-0.5 rounded-sm hover:bg-foreground/10 p-0.5"
                                      aria-label={`Revoke ${ROLE_LABELS[r.role]}`}
                                    >
                                      <X className="h-3 w-3" />
                                    </button>
                                  )}
                                </Badge>
                              );
                            })}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="outline" onClick={() => setAssignTarget(u)}>
                            <UserPlus className="h-4 w-4 mr-1" /> Assign
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AssignRoleDialog
        target={assignTarget}
        onClose={() => setAssignTarget(null)}
        onAssigned={() => {
          setAssignTarget(null);
          refetch();
        }}
      />
    </div>
  );
};

const AssignRoleDialog = ({
  target, onClose, onAssigned,
}: {
  target: UserAggregate | null;
  onClose: () => void;
  onAssigned: () => void;
}) => {
  const [role, setRole] = useState<AppRole>("events_admin");
  const [chapter, setChapter] = useState<Chapter>("all");
  const [busy, setBusy] = useState(false);

  useMemo(() => {
    setRole("events_admin");
    setChapter("all");
  }, [target?.user_id]);

  if (!target) return null;
  const def = ASSIGNABLE_ROLES.find((r) => r.value === role)!;

  const existing = target.roles.find(
    (r) =>
      r.role === role &&
      (def.chapterScoped ? r.chapter === chapter : true),
  );

  const submit = async () => {
    if (existing) {
      toast.error("This role is already assigned.");
      return;
    }
    setBusy(true);
    const { error } = await supabase.from("user_roles").insert({
      user_id: target.user_id,
      role,
      chapter: def.chapterScoped ? chapter : null,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Role assigned");
    onAssigned();
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Assign role</DialogTitle>
          <DialogDescription>
            For <strong>{target.name ?? target.email}</strong>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Role</Label>
            <Select value={role} onValueChange={(v: AppRole) => setRole(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {ASSIGNABLE_ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {def.chapterScoped && (
            <div className="space-y-1.5">
              <Label>Chapter scope</Label>
              <Select value={chapter} onValueChange={(v: Chapter) => setChapter(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="washington">Washington</SelectItem>
                  <SelectItem value="union">Union</SelectItem>
                  <SelectItem value="all">All Chapters</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {def.description}
              </p>
            </div>
          )}

          {existing && (
            <p className="text-xs text-destructive">
              This exact role is already assigned to this user.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !!existing}>
            {busy ? "Assigning..." : "Assign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminRolesList;
